import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, ChevronRight, Loader2, Plus, X, BarChart3, Upload, Shield, CheckCircle, Sparkles, Download, TrendingUp, AlertTriangle, Target, Clock, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link, useSearchParams } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Countdown Timer + Urgency Upsell
const UpsellBanner = () => {
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(t => t > 0 ? t - 1 : 0), 1000);
    return () => clearInterval(timer);
  }, []);
  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-purple-500/40 bg-gradient-to-r from-purple-500/10 via-neutral-900 to-indigo-500/10 p-8 mt-4">
      <div className="absolute top-0 right-0 w-40 h-40 bg-purple-500/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
      {/* Urgency bar */}
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2.5 mb-6 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-red-400 text-sm font-semibold">Exklusives Angebot – Nur jetzt nach deiner Analyse verfügbar</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-red-400" />
          <span className="text-red-400 font-mono font-bold text-sm">{String(mins).padStart(2,'0')}:{String(secs).padStart(2,'0')}</span>
        </div>
      </div>
      <div className="relative">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <p className="text-purple-400 text-sm font-semibold uppercase tracking-wider mb-2">Nur für dich, nur jetzt</p>
            <h3 className="text-2xl font-bold text-white mb-3">1:1 Persönliche Depot-Beratung</h3>
            <p className="text-neutral-400 leading-relaxed mb-3">Für nur <span className="text-white font-bold">69€</span> bekommst du eine <span className="text-white font-semibold">60-minütige persönliche Beratung</span> mit Bican, in der wir gemeinsam dein Depot durchgehen, alle deine Fragen beantworten und einen konkreten Aktionsplan für dich erstellen.</p>
            <p className="text-red-400/80 text-sm font-medium mb-4">Dieses Angebot ist exklusiv für Kunden nach der Depot-Analyse und nirgendwo anders buchbar.</p>
            <div className="flex flex-wrap gap-3 text-sm text-neutral-400">
              <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-purple-400" /> Persönliche Einschätzung zu jeder Position</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-purple-400" /> Alle deine Fragen beantwortet</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-purple-400" /> Konkreter Aktionsplan zum Umsetzen</span>
            </div>
          </div>
          <div className="flex-shrink-0 text-center">
            <a href="https://copecart.com/products/1fd5fca6/checkout" target="_blank" rel="noopener noreferrer"
              data-testid="upsell-cta"
              className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-400 text-white font-semibold px-8 py-4 rounded-full text-base transition-all shadow-[0_0_30px_rgba(168,85,247,0.3)] hover:shadow-[0_0_50px_rgba(168,85,247,0.5)] animate-pulse hover:animate-none">
              <Sparkles className="h-5 w-5" /> Jetzt Beratung sichern
            </a>
            <p className="text-neutral-600 text-xs mt-2">Einmalig 69€ – Kein Abo</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const STEPS = [
  { id: 'positions', title: 'Deine Positionen', subtitle: 'Welche Aktien oder ETFs hast du im Depot?' },
  { id: 'experience', title: 'Erfahrungslevel', subtitle: 'Wie lange investierst du schon?' },
  { id: 'depotSize', title: 'Depotgröße', subtitle: 'Wie groß ist dein Depot ungefähr?' },
  { id: 'investmentType', title: 'Anlagetyp', subtitle: 'Worin investierst du hauptsächlich?' },
  { id: 'goal', title: 'Anlageziel', subtitle: 'Was möchtest du mit deinem Depot erreichen?' },
  { id: 'horizon', title: 'Anlagehorizont', subtitle: 'Wie lange möchtest du investiert bleiben?' },
  { id: 'risk', title: 'Risikobereitschaft', subtitle: 'Wie viel Risiko bist du bereit einzugehen?' },
  { id: 'savingsRate', title: 'Monatliche Sparrate', subtitle: 'Wie viel investierst du monatlich?' },
  { id: 'checkFrequency', title: 'Depot-Kontrolle', subtitle: 'Wie oft schaust du in dein Depot?' },
  { id: 'decisionMaking', title: 'Kaufentscheidungen', subtitle: 'Wie triffst du deine Investmententscheidungen?' },
  { id: 'crashBehavior', title: 'Verhalten bei Kurseinbrüchen', subtitle: 'Wie reagierst du, wenn der Markt fällt?' },
  { id: 'contact', title: 'Fast geschafft!', subtitle: 'Name und E-Mail für deine Analyse' },
];

const OPTIONS = {
  experience: [
    { value: 'Anfänger', label: 'Anfänger', desc: 'Weniger als 1 Jahr' },
    { value: 'Fortgeschritten', label: 'Fortgeschritten', desc: '1–3 Jahre Erfahrung' },
    { value: 'Erfahren', label: 'Erfahren', desc: 'Mehr als 3 Jahre' },
  ],
  depotSize: [
    { value: 'Unter 5.000€', label: 'Unter 5.000€' },
    { value: '5.000€ – 25.000€', label: '5.000€ – 25.000€' },
    { value: '25.000€ – 100.000€', label: '25.000€ – 100.000€' },
    { value: 'Über 100.000€', label: 'Über 100.000€' },
  ],
  investmentType: [
    { value: 'Aktien', label: 'Aktien' },
    { value: 'ETFs', label: 'ETFs' },
    { value: 'Aktien & ETFs', label: 'Aktien & ETFs' },
    { value: 'Gemischt', label: 'Gemischt (Aktien, ETFs, Andere)' },
  ],
  goal: [
    { value: 'Vermögensaufbau', label: 'Vermögensaufbau', desc: 'Langfristig Vermögen aufbauen' },
    { value: 'Altersvorsorge', label: 'Altersvorsorge', desc: 'Für die Rente vorsorgen' },
    { value: 'Finanzielle Freiheit', label: 'Finanzielle Freiheit', desc: 'Unabhängig von Gehalt werden' },
    { value: 'Dividendeneinkommen', label: 'Dividendeneinkommen', desc: 'Regelmäßiges passives Einkommen' },
  ],
  horizon: [
    { value: 'Unter 5 Jahre', label: 'Unter 5 Jahre' },
    { value: '5–10 Jahre', label: '5–10 Jahre' },
    { value: '10–20 Jahre', label: '10–20 Jahre' },
    { value: 'Über 20 Jahre', label: 'Über 20 Jahre' },
  ],
  risk: [
    { value: 'Konservativ', label: 'Konservativ', desc: 'Sicherheit geht vor' },
    { value: 'Moderat', label: 'Moderat', desc: 'Ausgewogenes Verhältnis' },
    { value: 'Risikofreudig', label: 'Risikofreudig', desc: 'Höhere Rendite, höheres Risiko' },
  ],
  savingsRate: [
    { value: 'Unter 100€', label: 'Unter 100€' },
    { value: '100€ – 500€', label: '100€ – 500€' },
    { value: '500€ – 1.000€', label: '500€ – 1.000€' },
    { value: 'Über 1.000€', label: 'Über 1.000€' },
    { value: 'Unregelmäßig', label: 'Unregelmäßig', desc: 'Kein fester Sparplan' },
  ],
  checkFrequency: [
    { value: 'Täglich', label: 'Täglich', desc: 'Jeden Tag reinschauen' },
    { value: 'Wöchentlich', label: 'Wöchentlich' },
    { value: 'Monatlich', label: 'Monatlich' },
    { value: 'Selten', label: 'Selten', desc: 'Nur ab und zu' },
  ],
  decisionMaking: [
    { value: 'Eigene Analyse', label: 'Eigene Analyse', desc: 'Kennzahlen, Bilanzen, Bewertung' },
    { value: 'Social Media', label: 'Social Media / YouTube', desc: 'Tipps aus dem Internet' },
    { value: 'Empfehlungen', label: 'Empfehlungen', desc: 'Freunde, Familie, Berater' },
    { value: 'Bauchgefühl', label: 'Bauchgefühl', desc: 'Intuition und Trend' },
  ],
  crashBehavior: [
    { value: 'Nachkaufen', label: 'Nachkaufen', desc: 'Günstige Kurse nutzen' },
    { value: 'Halten', label: 'Halten & Abwarten', desc: 'Nichts verändern' },
    { value: 'Teilweise verkaufen', label: 'Teilweise verkaufen', desc: 'Verluste begrenzen' },
    { value: 'Panik', label: 'Alles verkaufen', desc: 'Sicherheit geht vor' },
  ],
};

// --- Sales Page ---
const COPECART_DEPOT_CHECK_URL = "https://copecart.com/products/218e5919/checkout";

const SalesPage = ({ onStartVerify }) => {
  const features = [
    'Objektive Analyse deines Depots (ohne Emotionen)',
    'Klare Aufdeckung von Schwächen & Fehlern',
    'Individuelles Risikoprofil für dein Portfolio',
    'Erkennung von Klumpenrisiken',
    'Analyse deiner Diversifikation (Branchen, Regionen)',
    'Bewertung deiner Performance im Marktvergleich',
    'Einschätzung deiner größten Positionen',
    'Konkrete Optimierungsvorschläge',
    'Strukturierte & leicht verständliche Auswertung',
    'Schnelle Ergebnisse ohne stundenlange Analyse',
  ];

  const handleBuy = () => {
    window.open(COPECART_DEPOT_CHECK_URL, '_blank');
    onStartVerify();
  };

  return (
  <div className="min-h-screen bg-neutral-950 text-white">
    <header className="border-b border-neutral-800/50 backdrop-blur-xl bg-neutral-950/80 sticky top-0 z-50">
      <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
        <Link to="/" className="flex items-center space-x-3">
          <img src="/bc-logo.png" alt="BC Logo" className="h-10 w-10 object-contain invert" />
          <span className="text-xl font-semibold text-white">Bican Coskun</span>
        </Link>
      </nav>
    </header>
    <main className="container mx-auto max-w-3xl px-6 py-16">
      {/* Hero */}
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-400 text-sm font-medium mb-6">
          <Sparkles className="h-4 w-4" /> Premium KI-Analyse
        </div>
        <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">Finde die Schwachstellen<br/>in deinem Depot</h1>
        <p className="text-lg md:text-xl text-neutral-400 max-w-xl mx-auto">Unsere KI analysiert jede Position, deckt Klumpenrisiken auf und gibt dir konkrete Optimierungsvorschläge.</p>
      </div>

      {/* So funktioniert's */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold text-center mb-10">So funktioniert's</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { num: '1', title: 'Screenshot hochladen', desc: 'Lade einen Screenshot deines Depots hoch – oder trage die Positionen manuell ein.' },
            { num: '2', title: 'KI analysiert dein Depot', desc: 'Unsere Premium-KI bewertet jede Position, findet Überschneidungen und prüft dein Risikoprofil.' },
            { num: '3', title: 'Premium-Bericht erhalten', desc: 'Du bekommst sofort eine ausführliche Analyse mit PDF-Download und konkreten Handlungsempfehlungen.' },
          ].map((s, i) => (
            <div key={i} className="text-center group">
              <div className="relative mx-auto w-16 h-16 mb-4 rounded-2xl bg-neutral-900 border border-neutral-800 group-hover:border-emerald-500/40 transition-all duration-300 flex items-center justify-center">
                <span className="text-2xl font-bold text-emerald-400">{s.num}</span>
              </div>
              <h3 className="text-base font-semibold mb-1">{s.title}</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 10 Checkmarks */}
      <div className="mb-10">
        <h2 className="text-2xl font-bold text-center mb-2">Das bekommst du im KI-Depotcheck</h2>
        <p className="text-neutral-500 text-center mb-8 text-sm">10 Analyse-Module in einem Bericht</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {features.map((text, i) => (
            <div key={i} className="flex items-center gap-3 bg-neutral-900/50 border border-neutral-800/50 rounded-xl p-4 hover:border-emerald-500/30 transition-all">
              <CheckCircle className="h-5 w-5 text-emerald-400 flex-shrink-0" />
              <p className="text-sm font-medium text-white">{text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Analyse-Vorschau */}
      <div className="mb-10">
        <h2 className="text-2xl font-bold text-center mb-6">So sieht dein Ergebnis aus</h2>
        <div className="relative rounded-2xl overflow-hidden border border-neutral-800 bg-neutral-900 shadow-2xl shadow-emerald-500/5">
          <img src="/analysis-preview.png" alt="Beispiel einer KI-Depot-Analyse" className="w-full object-cover max-h-[420px]" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
            <p className="text-neutral-400 text-sm mb-3">Beispiel-Auszug einer Premium-Analyse</p>
          </div>
        </div>
      </div>

      {/* Pricing CTA */}
      <div className="bg-gradient-to-r from-purple-500/10 to-emerald-500/10 border border-purple-500/20 rounded-2xl p-8 text-center mb-8">
        <p className="text-sm text-neutral-400 uppercase tracking-wider mb-2 font-medium">Einmalige Zahlung</p>
        <div className="text-5xl font-bold mb-1">6,99€</div>
        <p className="text-neutral-500 mb-6 text-sm">Kein Abo. Sofort verfügbar.</p>
        <Button
          data-testid="buy-now-btn"
          onClick={handleBuy}
          className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-12 py-6 text-lg rounded-full transition-all duration-300 shadow-[0_0_30px_rgba(52,211,153,0.3)] hover:shadow-[0_0_50px_rgba(52,211,153,0.5)] w-full sm:w-auto"
        >
          <Sparkles className="h-5 w-5 mr-2" />
          Jetzt Depot-Check kaufen
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-neutral-500">
        <div className="flex items-center gap-2"><Shield className="h-4 w-4" /> Sichere Zahlung via CopeCart</div>
        <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4" /> Sofortiges Ergebnis</div>
        <div className="flex items-center gap-2"><Download className="h-4 w-4" /> PDF-Bericht inklusive</div>
      </div>

      <p className="text-neutral-600 text-xs text-center mt-10 max-w-xl mx-auto leading-relaxed">Hinweis: Der KI-Depot-Check stellt keine Anlageberatung dar. Die KI-Analyse ersetzt keine eigenständige Recherche oder professionelle Beratung. Investitionen in Aktien und Wertpapiere sind mit Risiken verbunden und sollten verantwortungsbewusst getätigt werden.</p>

      {/* Already paid link */}
      <div className="text-center mt-8">
        <button data-testid="already-paid-btn" onClick={onStartVerify} className="text-neutral-500 hover:text-emerald-400 text-sm underline underline-offset-4 transition-colors">
          Ich habe bereits bezahlt
        </button>
      </div>
    </main>
  </div>
  );
};

// --- Payment Verification (contact fallback) ---
const VerifyPayment = () => {
  return (
    <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center">
      <div className="max-w-md w-full mx-auto px-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
          <Shield className="h-8 w-8 text-emerald-400" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Du hast bereits bezahlt?</h2>
        <p className="text-neutral-400 text-sm mb-6">Wenn du bereits über CopeCart bezahlt hast und nicht automatisch weitergeleitet wurdest, kontaktiere uns bitte direkt:</p>
        <a href="mailto:infobicancoskun@gmail.com" data-testid="contact-email-link" className="inline-flex items-center gap-2 bg-neutral-800 border border-neutral-700 hover:border-emerald-500/40 rounded-xl px-6 py-4 text-emerald-400 font-medium transition-all">
          <Mail className="h-5 w-5" /> infobicancoskun@gmail.com
        </a>
        <p className="text-neutral-500 text-sm mt-4">Wir schalten deinen Zugang innerhalb weniger Minuten frei.</p>
        <p className="text-neutral-600 text-xs mt-6">Die Abbuchung erfolgt durch CopeCart.</p>
      </div>
    </div>
  );
};


const DepotCheck = () => {
  const [searchParams] = useSearchParams();
  const isStartPath = window.location.pathname === '/depotcheck/start';
  const isBezahltPath = window.location.pathname === '/depotcheck/bezahlt';
  const isTestPath = window.location.pathname === '/depotcheck/test';

  const [paid, setPaid] = useState(isBezahltPath);
  const [sessionId, setSessionId] = useState('');
  const [checkingPayment, setCheckingPayment] = useState(isTestPath || isBezahltPath);
  const [showVerify, setShowVerify] = useState(isStartPath);

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [pdfId, setPdfId] = useState(null);
  const [formData, setFormData] = useState({
    experience: '', depotSize: '', investmentType: '', goal: '', horizon: '',
    risk: '', savingsRate: '', checkFrequency: '', decisionMaking: '', crashBehavior: '',
    positions: [{ name: '', quantity: '', value: '' }],
    name: '', email: '', phone: '', newsletter: false,
  });
  const [extracting, setExtracting] = useState(false);
  const [screenshotsUploaded, setScreenshotsUploaded] = useState(false);
  const fileInputRef = useRef(null);

  // Bezahlt mode: create paid session directly (customer comes from CopeCart)
  useEffect(() => {
    if (!isBezahltPath) return;
    const createPaidSession = async () => {
      try {
        const res = await fetch(`${API_URL}/api/depotcheck/test-session`, { method: 'POST' });
        const data = await res.json();
        if (data.session_id) {
          setPaid(true);
          setSessionId(data.session_id);
        }
      } catch {}
      setCheckingPayment(false);
    };
    createPaidSession();
  }, [isBezahltPath]);

  // Test mode: create fake paid session
  useEffect(() => {
    if (!isTestPath) return;
    const createTestSession = async () => {
      try {
        const res = await fetch(`${API_URL}/api/depotcheck/test-session`, { method: 'POST' });
        const data = await res.json();
        if (data.session_id) {
          setPaid(true);
          setSessionId(data.session_id);
        }
      } catch {}
      setCheckingPayment(false);
    };
    createTestSession();
  }, [isTestPath]);

  const handleVerified = (sid, email) => {
    setSessionId(sid);
    setPaid(true);
    setShowVerify(false);
    if (email) setFormData(prev => ({ ...prev, email }));
  };

  const currentStepId = STEPS[step].id;
  const canProceed = () => {
    if (currentStepId === 'positions') return formData.positions.some(p => p.name.trim() !== '');
    if (currentStepId === 'contact') return formData.name.trim() !== '' && formData.email.trim() !== '';
    return formData[currentStepId] !== '';
  };

  const addPosition = () => setFormData(prev => ({ ...prev, positions: [...prev.positions, { name: '', quantity: '', value: '' }] }));
  const removePosition = (idx) => setFormData(prev => ({ ...prev, positions: prev.positions.filter((_, i) => i !== idx) }));
  const updatePosition = (idx, field, val) => {
    setFormData(prev => { const p = [...prev.positions]; p[idx] = { ...p[idx], [field]: val }; return { ...prev, positions: p }; });
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader(); reader.readAsDataURL(file); reader.onload = () => resolve(reader.result); reader.onerror = reject;
  });

  const handleScreenshots = async (files) => {
    if (!files.length) return;
    setExtracting(true);
    setScreenshotsUploaded(true);
    try {
      const images = await Promise.all(Array.from(files).map(f => toBase64(f)));
      const res = await fetch(`${API_URL}/api/depotcheck/extract-positions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ images }),
      });
      const result = await res.json();
      if (result.positions?.length) setFormData(prev => ({ ...prev, positions: result.positions }));
    } catch {}
    setExtracting(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/depotcheck`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, session_id: sessionId }),
      });
      const data = await res.json();

      // Already complete (cached result)
      if (data.status === 'complete' && data.analysis_data) {
        setAnalysisData(data.analysis_data);
        setPdfId(data.pdf_id);
        setLoading(false);
        return;
      }

      // Async: poll for result
      if (data.task_id) {
        const taskId = data.task_id;
        for (let i = 0; i < 60; i++) {
          await new Promise(r => setTimeout(r, 3000));
          try {
            const pollRes = await fetch(`${API_URL}/api/depotcheck/result/${taskId}`);
            const pollData = await pollRes.json();
            if (pollData.status === 'complete' && pollData.analysis_data) {
              setAnalysisData(pollData.analysis_data);
              setPdfId(pollData.pdf_id);
              setLoading(false);
              return;
            }
            if (pollData.status === 'error') {
              setAnalysisData({ error: true, message: pollData.detail || 'Analyse fehlgeschlagen' });
              setLoading(false);
              return;
            }
          } catch {}
        }
        setAnalysisData({ error: true, message: 'Zeitüberschreitung. Bitte erneut versuchen.' });
      } else {
        setAnalysisData({ error: true, message: data.detail || 'Unbekannter Fehler' });
      }
    } catch { setAnalysisData({ error: true, message: 'Verbindungsfehler. Bitte erneut versuchen.' }); }
    setLoading(false);
  };

  const nextStep = () => { if (step === STEPS.length - 1) handleSubmit(); else setStep(s => s + 1); };

  const OptionCard = ({ selected, onClick, label, desc }) => (
    <button data-testid={`option-${label}`} onClick={onClick}
      className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 ${selected ? 'border-emerald-400 bg-emerald-400/10 shadow-[0_0_20px_rgba(52,211,153,0.15)]' : 'border-neutral-700 bg-neutral-800/50 hover:border-neutral-500'}`}>
      <span className="text-lg font-medium text-white">{label}</span>
      {desc && <p className="text-sm text-neutral-400 mt-1">{desc}</p>}
    </button>
  );

  // --- RENDER LOGIC ---

  // Checking payment
  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center">
        <div className="text-center space-y-6">
          <Loader2 className="h-12 w-12 text-emerald-400 animate-spin mx-auto" />
          <p className="text-xl text-neutral-300">Zahlung wird überprüft...</p>
        </div>
      </div>
    );
  }

  // Not paid yet → show verify or sales page
  if (!paid) {
    if (showVerify) return <VerifyPayment />;
    return <SalesPage onStartVerify={() => setShowVerify(true)} />;
  }

  // Analysis result
  if (analysisData) {
    if (analysisData.error) {
      return (
        <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center">
          <div className="text-center space-y-4 max-w-md mx-auto px-6">
            <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto" />
            <p className="text-xl font-semibold">Fehler bei der Analyse</p>
            <p className="text-neutral-400 text-sm">{analysisData.message || 'Ein unerwarteter Fehler ist aufgetreten.'}</p>
            <p className="text-neutral-500 text-xs">Unser Team wurde automatisch benachrichtigt. Bei Fragen schreibe uns an infobicancoskun@gmail.com</p>
            <Button onClick={() => setAnalysisData(null)} className="bg-emerald-500 text-black rounded-full px-8">Nochmal versuchen</Button>
          </div>
        </div>
      );
    }

    const CHART_COLORS = ['#34d399','#6366f1','#f472b6','#fbbf24','#14b8a6','#ef4444','#a855f7','#f59e0b','#06b6d4','#84cc16'];
    const ratingStyles = { solid: { bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: CheckCircle }, speculative: { bg: 'bg-amber-500/15', border: 'border-amber-500/30', text: 'text-amber-400', icon: AlertTriangle }, problematic: { bg: 'bg-red-500/15', border: 'border-red-500/30', text: 'text-red-400', icon: AlertTriangle } };
    const prioStyles = { high: 'border-red-500/40 bg-red-500/5', medium: 'border-amber-500/40 bg-amber-500/5', low: 'border-emerald-500/40 bg-emerald-500/5' };
    const prioLabels = { high: 'Hoch', medium: 'Mittel', low: 'Niedrig' };
    const prioColors = { high: 'text-red-400', medium: 'text-amber-400', low: 'text-emerald-400' };
    const gradeColors = { A: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10', B: 'text-indigo-400 border-indigo-500/40 bg-indigo-500/10', C: 'text-amber-400 border-amber-500/40 bg-amber-500/10', D: 'text-red-400 border-red-500/40 bg-red-500/10', F: 'text-red-400 border-red-500/40 bg-red-500/10' };
    const ov = analysisData.depot_overview || {};
    const risk = analysisData.risk_assessment || {};
    const concl = analysisData.conclusion || {};
    const sectors = (analysisData.sector_allocation || []).map((s, i) => ({ ...s, color: CHART_COLORS[i % CHART_COLORS.length] }));
    const regions = (analysisData.region_allocation || []).map((s, i) => ({ ...s, color: CHART_COLORS[i % CHART_COLORS.length] }));

    return (
      <div className="min-h-screen bg-neutral-950 text-white">
        <header className="border-b border-neutral-800/50 backdrop-blur-xl bg-neutral-950/80 sticky top-0 z-50">
          <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/bc-logo.png" alt="BC Logo" className="h-10 w-10 object-contain invert" />
              <span className="text-xl font-semibold text-white">Bican Coskun</span>
            </Link>
            {pdfId && (
              <a data-testid="download-pdf-btn" href={`${API_URL}/api/depotcheck/pdf/${pdfId}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-5 py-2.5 rounded-full text-sm transition-all">
                <Download className="h-4 w-4" /> PDF herunterladen
              </a>
            )}
          </nav>
        </header>
        <main data-testid="analysis-result" className="container mx-auto max-w-4xl px-4 sm:px-6 py-10 sm:py-16 space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-emerald-400 text-sm font-medium mb-4">
              <BarChart3 className="h-4 w-4" /> Premium-Analyse abgeschlossen
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-3">Deine Depot-Analyse</h1>
          </div>

          {/* Overview Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {concl.grade && (
              <div className={`bg-neutral-900 border rounded-xl p-4 text-center ${gradeColors[concl.grade] || 'border-neutral-800'}`}>
                <p className="text-3xl font-bold">{concl.grade}</p>
                <p className="text-xs text-neutral-500 mt-1">{concl.grade_label || 'Note'}</p>
              </div>
            )}
            <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-emerald-400">{ov.total_positions || '-'}</p>
              <p className="text-xs text-neutral-500 mt-1">Positionen</p>
            </div>
            <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-4 text-center">
              <p className="text-base font-bold text-white leading-tight break-words">{ov.estimated_value || '-'}</p>
              <p className="text-xs text-neutral-500 mt-1">Gesch. Wert</p>
            </div>
            <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold" style={{ color: risk.score >= 7 ? '#ef4444' : risk.score >= 4 ? '#fbbf24' : '#34d399' }}>{risk.score || '-'}/10</p>
              <p className="text-xs text-neutral-500 mt-1">Risiko</p>
            </div>
            <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-white leading-tight">{ov.diversification_score || '-'}</p>
              <p className="text-xs text-neutral-500 mt-1">Diversifikation</p>
            </div>
            <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-4 text-center">
              <p className="text-sm font-bold leading-tight">{risk.fits_profile ? <span className="text-emerald-400">Passt zum Profil</span> : <span className="text-red-400">Passt nicht zum Profil</span>}</p>
              <p className="text-xs text-neutral-500 mt-1">Risikoprofil</p>
            </div>
          </div>

          {/* Overview Text */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6">
            <h2 className="text-lg font-bold text-emerald-400 mb-3 flex items-center gap-2"><BarChart3 className="h-5 w-5" /> Depot-Übersicht</h2>
            <p className="text-neutral-300 leading-relaxed">{ov.summary}</p>
          </div>

          {/* Allocation Charts */}
          <div className="grid md:grid-cols-2 gap-4">
            {[{ title: 'Sektor-Verteilung', data: sectors, key: 'sector' }, { title: 'Regionen-Verteilung', data: regions, key: 'region' }].map(chart => (
              <div key={chart.key} className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6">
                <h3 className="text-base font-bold text-white mb-4">{chart.title}</h3>
                <div className="flex items-center gap-4">
                  <div className="w-40 h-40 flex-shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={chart.data} dataKey="percentage" nameKey={chart.key} cx="50%" cy="50%" outerRadius={65} innerRadius={35} paddingAngle={2} stroke="none">
                          {chart.data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Pie>
                        <Tooltip formatter={(v) => `${Math.round(v)}%`} contentStyle={{ background: '#171717', border: '1px solid #333', borderRadius: '8px', color: '#fff', fontSize: '12px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-1.5">
                    {chart.data.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: item.color }} />
                        <span className="text-neutral-400 flex-1 truncate">{item[chart.key]}</span>
                        <span className="text-white font-medium">{Math.round(item.percentage)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Position Analyses */}
          <div>
            <h2 className="text-lg font-bold text-white mb-4">Analyse der Positionen</h2>
            <div className="space-y-3">
              {(analysisData.positions || []).map((pos, i) => {
                const style = ratingStyles[pos.rating] || ratingStyles.solid;
                const Icon = style.icon;
                return (
                  <div key={i} className={`${style.bg} border ${style.border} rounded-xl p-5`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Icon className={`h-5 w-5 ${style.text}`} />
                        <span className="font-semibold text-white">{pos.name}</span>
                        <span className="text-xs text-neutral-500 bg-neutral-800 px-2 py-0.5 rounded">{pos.type}</span>
                        {pos.sector && <span className="text-xs text-neutral-600 bg-neutral-800/50 px-2 py-0.5 rounded">{pos.sector}</span>}
                      </div>
                      <span className={`text-xs font-medium ${style.text} px-2 py-1 rounded-full border ${style.border}`}>{pos.rating_label}</span>
                    </div>
                    {pos.fundamentals && <p className="text-xs text-indigo-400 italic mb-2">{pos.fundamentals}</p>}
                    <p className="text-sm text-neutral-300 leading-relaxed mb-2">{pos.assessment}</p>
                    {pos.correlation_note && (
                      <div className="mt-2 pt-2 border-t border-white/5">
                        <p className="text-xs text-purple-400 italic flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Korrelation: {pos.correlation_note}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Correlation Matrix */}
          {analysisData.correlation_matrix && (
            <div className="bg-neutral-900 border border-indigo-500/20 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-indigo-400 mb-3 flex items-center gap-2"><TrendingUp className="h-5 w-5" /> Korrelationsanalyse</h2>
              <p className="text-neutral-300 leading-relaxed">{analysisData.correlation_matrix}</p>
            </div>
          )}

          {/* Overlaps */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6">
            <h2 className="text-lg font-bold text-amber-400 mb-3 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Überschneidungen & Doppelungen</h2>
            <p className="text-neutral-300 leading-relaxed">{analysisData.overlaps}</p>
          </div>

          {/* Risk Assessment */}
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Shield className="h-5 w-5 text-purple-400" /> Risikobewertung</h2>
            <div className="flex items-center gap-4 mb-3">
              <div className="flex-1 bg-neutral-800 rounded-full h-4 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${(risk.score || 0) * 10}%`, background: risk.score >= 7 ? '#ef4444' : risk.score >= 4 ? '#fbbf24' : '#34d399' }} />
              </div>
              <span className="text-xl font-bold" style={{ color: risk.score >= 7 ? '#ef4444' : risk.score >= 4 ? '#fbbf24' : '#34d399' }}>{risk.score}/10</span>
            </div>
            {(risk.volatility_estimate || risk.max_drawdown_scenario) && (
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                {risk.volatility_estimate && (
                  <div className="bg-neutral-800/50 rounded-lg p-3">
                    <p className="text-xs text-neutral-500 uppercase font-medium">Volatilität</p>
                    <p className="text-sm text-white font-semibold">{risk.volatility_estimate}</p>
                  </div>
                )}
                {risk.max_drawdown_scenario && (
                  <div className="bg-neutral-800/50 rounded-lg p-3">
                    <p className="text-xs text-neutral-500 uppercase font-medium">Krisen-Szenario</p>
                    <p className="text-sm text-white">{risk.max_drawdown_scenario}</p>
                  </div>
                )}
              </div>
            )}
            <p className="text-neutral-300 leading-relaxed">{risk.details}</p>
          </div>

          {/* Optimization Suggestions */}
          <div>
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Target className="h-5 w-5 text-emerald-400" /> Optimierungsvorschläge</h2>
            <div className="space-y-3">
              {(analysisData.optimizations || []).map((opt, i) => (
                <div key={i} className={`border rounded-xl p-5 ${prioStyles[opt.priority] || prioStyles.medium}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-bold uppercase ${prioColors[opt.priority] || prioColors.medium}`}>Priorität: {prioLabels[opt.priority] || opt.priority}</span>
                  </div>
                  <h3 className="font-semibold text-white mb-1">{opt.title}</h3>
                  <p className="text-sm text-neutral-300 leading-relaxed">{opt.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Conclusion */}
          <div className="bg-gradient-to-br from-emerald-500/10 to-purple-500/10 border border-emerald-500/20 rounded-2xl p-6">
            <h2 className="text-lg font-bold text-emerald-400 mb-4">Fazit</h2>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
                <p className="text-xs text-emerald-400 font-bold uppercase mb-1">Stärke</p>
                <p className="text-sm text-neutral-300">{concl.strength}</p>
              </div>
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <p className="text-xs text-red-400 font-bold uppercase mb-1">Schwäche</p>
                <p className="text-sm text-neutral-300">{concl.weakness}</p>
              </div>
            </div>
            {concl.one_year_outlook && (
              <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-4 mb-4">
                <p className="text-xs text-indigo-400 font-bold uppercase mb-1">12-Monats-Ausblick</p>
                <p className="text-sm text-neutral-300">{concl.one_year_outlook}</p>
              </div>
            )}
            <p className="text-neutral-300 leading-relaxed">{concl.summary}</p>
          </div>

          {/* PDF Download CTA */}
          {pdfId && (
            <div className="text-center pt-4">
              <a href={`${API_URL}/api/depotcheck/pdf/${pdfId}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-10 py-4 rounded-full text-lg transition-all shadow-[0_0_30px_rgba(52,211,153,0.3)]">
                <Download className="h-5 w-5" /> Analyse als PDF herunterladen
              </a>
              <p className="text-neutral-500 text-sm mt-3">Professioneller Bericht mit allen Details und Rechnung</p>
            </div>
          )}

          {/* Upsell Banner – Persönliche Beratung mit Verknappung */}
          <UpsellBanner />

          <p className="text-neutral-600 text-xs text-center mt-8 max-w-xl mx-auto leading-relaxed">Hinweis: Diese Analyse stellt keine Anlageberatung dar. Die KI-Analyse ersetzt keine eigenständige Recherche oder professionelle Beratung. Investitionen in Aktien und Wertpapiere sind mit Risiken verbunden und sollten verantwortungsbewusst getätigt werden.</p>
        </main>
      </div>
    );
  }

  // Loading analysis
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center">
        <div className="text-center space-y-6">
          <Loader2 className="h-12 w-12 text-emerald-400 animate-spin mx-auto" />
          <p className="text-xl text-neutral-300">Deine Premium-Analyse wird erstellt...</p>
          <p className="text-sm text-neutral-500">Das kann bis zu 30 Sekunden dauern</p>
        </div>
      </div>
    );
  }

  // --- Wizard ---
  const renderStep = () => {
    if (currentStepId === 'positions') {
      const hasExtracted = screenshotsUploaded && formData.positions.some(p => p.name?.trim());
      return (
        <div className="space-y-6">
          <div data-testid="screenshot-upload" onClick={() => !extracting && fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); }} onDrop={(e) => { e.preventDefault(); handleScreenshots(e.dataTransfer.files); }}
            className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all duration-200 ${extracting ? 'border-purple-500/50 bg-purple-500/5' : hasExtracted ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-neutral-700 hover:border-purple-400/50 hover:bg-purple-500/5'}`}>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleScreenshots(e.target.files)} />
            {extracting ? (
              <div className="space-y-3"><Loader2 className="h-8 w-8 text-purple-400 animate-spin mx-auto" /><p className="text-purple-300 font-medium">KI liest Positionen aus...</p></div>
            ) : hasExtracted ? (
              <div className="space-y-2"><p className="text-emerald-400 font-medium">{formData.positions.filter(p => p.name?.trim()).length} Positionen erkannt – unten bearbeiten</p><p className="text-xs text-neutral-500">Klicke, um weitere Screenshots hinzuzufügen</p></div>
            ) : (
              <div className="space-y-2"><div className="mx-auto w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center"><Upload className="h-5 w-5 text-purple-400" /></div><p className="text-white font-medium">Depot-Screenshot hochladen</p><p className="text-xs text-neutral-500">KI liest Positionen automatisch aus</p></div>
            )}
          </div>
          {!hasExtracted && <div className="flex items-center gap-4"><div className="flex-1 h-px bg-neutral-800" /><span className="text-neutral-500 text-sm">oder manuell eingeben</span><div className="flex-1 h-px bg-neutral-800" /></div>}
          <div className="space-y-3">
            {formData.positions.map((pos, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <div className="flex-1 space-y-2">
                  <input data-testid={`position-name-${idx}`} type="text" value={pos.name || ''} onChange={(e) => updatePosition(idx, 'name', e.target.value)} placeholder={`Position ${idx + 1} (z.B. Apple, MSCI World)`} className="w-full bg-neutral-800/50 border-2 border-neutral-700 rounded-xl px-4 py-3 text-white placeholder-neutral-500 focus:border-emerald-400 focus:outline-none transition-colors text-sm" />
                  <div className="flex gap-2">
                    <input data-testid={`position-qty-${idx}`} type="text" value={pos.quantity || ''} onChange={(e) => updatePosition(idx, 'quantity', e.target.value)} placeholder="Anzahl" className="flex-1 bg-neutral-800/50 border-2 border-neutral-700 rounded-xl px-4 py-2.5 text-white placeholder-neutral-500 focus:border-emerald-400 focus:outline-none transition-colors text-xs" />
                    <input data-testid={`position-val-${idx}`} type="text" value={pos.value || ''} onChange={(e) => updatePosition(idx, 'value', e.target.value)} placeholder="Wert (z.B. 2.500€)" className="flex-1 bg-neutral-800/50 border-2 border-neutral-700 rounded-xl px-4 py-2.5 text-white placeholder-neutral-500 focus:border-emerald-400 focus:outline-none transition-colors text-xs" />
                  </div>
                </div>
                {formData.positions.length > 1 && <button onClick={() => removePosition(idx)} className="p-2 mt-2 text-neutral-500 hover:text-red-400"><X className="h-5 w-5" /></button>}
              </div>
            ))}
          </div>
          <button data-testid="add-position-btn" onClick={addPosition} className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm font-medium"><Plus className="h-4 w-4" /> Weitere Position</button>
        </div>
      );
    }
    if (currentStepId === 'contact') {
      return (
        <div className="space-y-5">
          <input data-testid="name-input" type="text" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Dein Name" className="w-full bg-neutral-800/50 border-2 border-neutral-700 rounded-xl px-5 py-4 text-white placeholder-neutral-500 focus:border-emerald-400 focus:outline-none transition-colors" />
          <input data-testid="email-input" type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="Deine E-Mail-Adresse" className="w-full bg-neutral-800/50 border-2 border-neutral-700 rounded-xl px-5 py-4 text-white placeholder-neutral-500 focus:border-emerald-400 focus:outline-none transition-colors" />
          <label className="flex items-start gap-3 cursor-pointer mt-4">
            <input data-testid="newsletter-checkbox" type="checkbox" checked={formData.newsletter} onChange={(e) => setFormData(p => ({ ...p, newsletter: e.target.checked }))} className="mt-1 h-5 w-5 rounded border-neutral-600 bg-neutral-800 accent-emerald-500" />
            <span className="text-sm text-neutral-400">Ich möchte weitere Aktien- & KI-Tipps per E-Mail erhalten. Abmeldung jederzeit möglich.</span>
          </label>
        </div>
      );
    }
    const opts = OPTIONS[currentStepId];
    if (!opts) return null;
    return (
      <div className="space-y-3">
        {opts.map(opt => <OptionCard key={opt.value} selected={formData[currentStepId] === opt.value} onClick={() => setFormData(p => ({ ...p, [currentStepId]: opt.value }))} label={opt.label} desc={opt.desc} />)}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <header className="border-b border-neutral-800/50 backdrop-blur-xl bg-neutral-950/80 sticky top-0 z-50">
        <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <img src="/bc-logo.png" alt="BC Logo" className="h-10 w-10 object-contain invert" />
            <span className="text-xl font-semibold text-white">Bican Coskun</span>
          </Link>
        </nav>
      </header>
      <main className="container mx-auto max-w-xl px-6 py-16">
        <div className="flex items-center gap-1 mb-12">
          {STEPS.map((s, i) => <div key={s.id} className={`h-1 rounded-full flex-1 transition-all duration-500 ${i <= step ? 'bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.4)]' : 'bg-neutral-800'}`} />)}
        </div>
        <div className="mb-8">
          <p className="text-emerald-400 text-sm font-medium mb-2">Schritt {step + 1} von {STEPS.length}</p>
          <h2 className="text-3xl font-bold mb-2">{STEPS[step].title}</h2>
          <p className="text-neutral-400">{STEPS[step].subtitle}</p>
        </div>
        <div className="mb-10">{renderStep()}</div>
        <div className="flex justify-between items-center">
          {step > 0 ? <button data-testid="back-btn" onClick={() => setStep(s => s - 1)} className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors"><ArrowLeft className="h-4 w-4" /> Zurück</button> : <div />}
          <Button data-testid="next-btn" onClick={nextStep} disabled={!canProceed()}
            className={`px-8 py-5 text-base rounded-full font-semibold transition-all duration-300 ${canProceed() ? 'bg-emerald-500 hover:bg-emerald-400 text-black shadow-[0_0_25px_rgba(52,211,153,0.3)]' : 'bg-neutral-800 text-neutral-500 cursor-not-allowed'}`}>
            {step === STEPS.length - 1 ? 'Analyse starten' : 'Weiter'}<ChevronRight className="ml-1 h-5 w-5" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default DepotCheck;
